import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { defaultTo, get, isEmpty, some } from 'lodash';
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useGetOfflineSuppliersApi from 'src/services/catalog_products/useGetOfflineSupplierQuery';
import { OfflineSupplier } from 'src/types/catalog/Supplier';
import { DropDownCategories, SetState } from 'src/types/generic';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import usePostMessage, { handlePostMessage } from 'src/hooks/usePostMessage';
import UnsavedSelectionDialog from 'src/sections/catalog_products/UnsavedSelectionDialog';
import useUnsavedSelection from 'src/sections/catalog_products/useUnsavedSelection';
import useUpdateCartMutation from 'src/services/cart/useUpdateCartMutation';
import { CartItem } from 'src/types/cart';
import {
  CatalogTreeViewItem,
  GetProductsResponse,
  Product,
  ProductDetails,
  ProductListFilterRequest,
  ProductSelections,
} from 'src/types/catalog';

import { ProductFilterCategories } from 'src/constants/productFilterCategories';
import useDebounce from 'src/hooks/useDebounce';
import {
  applyItemFilterToGridFilter,
  clearContractedItemGridFilter,
  EMPTY_GRID_FILTER,
  getAttributesFilterKey,
  removeFieldFromFilter,
  shouldClearAttributesForContractedItems,
  shouldResetItemFilterForAttributes,
} from './catalogProductContext.utils';

import { queryKeys } from 'src/constants/querykeys';
import { fetchProductDetailsQuery } from 'src/services/catalog_products/productDetailsQueryFn';
import useGetCatalogsQuery from 'src/services/catalog_products/useGetCatalogsQuery';
import useGetCategoryQuery from 'src/services/catalog_products/useGetCategoryQuery';
import useGetProductDetailsQuery from 'src/services/catalog_products/useGetProductDetailsQuery';
import { useGetProductsQuery } from 'src/services/catalog_products/useGetProductsQuery';
import useAddToFavoritesMutation from 'src/services/favorite/useAddToFavoritesMutation';
import useGetFavoriteFoldersQuery from 'src/services/favorite/useGetFavoriteFoldersQuery';

import { IsSupplierUser } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { RecommendPageParams } from 'src/types/recommendation_items';
import { TreeViewItem } from 'src/types/TreeView';
import {
  getCartItemProductKey,
  mapProductsToFavoriteItems,
  mapSelectionsToCartItems,
  removeProductSelection,
} from 'src/utils/productActionMappers';
import { modifyRequest } from 'src/utils/requestInterceptor';
import {
  getCatalogDropdownPlaceholder,
  getNumericCatalogId,
} from 'src/utils/tansformCatalogsToTreeData';
import { createContext } from 'use-context-selector';

export interface ProductSelectionState {
  selected: boolean;
  qty: number;
}

export const PRESERVE_SORT_FIELDS = ['price', 'description', 'itemNum'];

export interface ViewCatalogProductsContextShape {
  dataState: State;
  setDataState: SetState<State>;
  catalogs: CatalogTreeViewItem[] | undefined;
  loadingCatalogs: boolean;

  categories: TreeViewItem[] | undefined;
  loadingCategories: boolean;
  fetchCategories: () => Promise<QueryObserverResult<TreeViewItem[] | undefined>>;

  products: Product[];
  productsTotal: number;
  loadingProducts: boolean;
  fetchProducts: () => Promise<QueryObserverResult<GetProductsResponse | undefined>>;

  productDetails: ProductDetails | undefined;
  loadingProductDetails: boolean;
  fetchProductDetails: () => Promise<QueryObserverResult<ProductDetails | undefined>>;

  selectedView: number;
  setSelectedView: SetState<number>;

  search: string;
  setSearch: SetState<string>;
  handleSearchChange: (value: string) => Promise<void>;

  selectedCatalog: CatalogTreeViewItem | null;
  setSelectedCatalog: SetState<CatalogTreeViewItem | null>;

  selectedCategories: number[];
  setSelectedCategories: SetState<number[]>;
  appliedCategoryIds: number[];
  setAppliedCategoryIds: SetState<number[]>;

  productSelections: Record<string, ProductSelectionState>;
  setProductSelections: SetState<Record<string, ProductSelectionState>>;

  selectedProduct: Product | null;
  setSelectedProduct: SetState<Product | null>;

  handleCatalogChange: (catalog: CatalogTreeViewItem | null) => Promise<void>;
  handleCategoryChange: (checkedItems: string[]) => Promise<void>;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  updateProductSelection: (productKey: string, updates: Partial<ProductSelectionState>) => void;
  getProductSelection: (productKey: string) => ProductSelectionState;
  hasSelectedItems: boolean;
  clearSelections: () => void;
  getSelectedItems: () => {
    productKey: string;
  }[];
  handleSingleItemAddToCart: (items: CartItem) => Promise<void>;
  selectedCategoryIds: number[];
  setSelectedCategoryIds: SetState<number[]>;
  isAllCategoriesSelected: boolean;
  setIsAllCategoriesSelected: SetState<boolean>;
  isOfflineCatalog: boolean;
  supplierData: OfflineSupplier[];
  handleSupplierChange: (selectedSupplierIds: number[]) => Promise<void>;
  isLoadingSuppliers: boolean;
  appliedSupplierIds: number[];
  setAppliedSupplierIds: SetState<number[]>;
  onDataStateChange: (event: GridDataStateChangeEvent | State) => Promise<void>;
  onItemChange: (event: GridItemChangeEvent) => void;
  handleOpenProductPanel: () => void;
  handleCloseProductPanel: () => void;
  handleViewProductDetails: (e: MouseEvent, product: Product) => Promise<void>;
  isFetchingProductDetails: boolean;

  categoryDropdownPlaceholder: string;
  catalogDropdownPlaceholder: string;

  isProductPanelOpen: boolean;
  setIsProductPanelOpen: SetState<boolean>;

  // Add to shopping list
  folderOptions: Array<{ id: number; name: string }> | undefined;
  handleAddToFavorites: (folderId: number) => Promise<void>;
  isAddingToFavorites: boolean;
  itemFilter: DropDownCategories;
  setItemFilter: SetState<DropDownCategories>;
  setPendingFilterToastKey: SetState<string | null>;
  handleSelectedItemsAddToCart: () => Promise<void>;
  isUpdatingCart: boolean;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  handleConfirm: (callback: () => void | Promise<void>) => Promise<void>;
  getTakeForView: (viewIndex: number) => number;

  shouldPreserveSort: (currentSortArray: Array<SortDescriptor>) => boolean;
  isSwapItemModalOpen: boolean;
  setIsSwapItemModalOpen: SetState<boolean>;
  swapItemData: RecommendPageParams | null;
  setSwapItemData: SetState<RecommendPageParams | null>;
  //supplier permission
  isSupplierUser: boolean;
}
export interface ViewCatalogProductsContextProps extends PropsWithChildren {}

export const ViewCatalogProductsContext = createContext<ViewCatalogProductsContextShape>(
  {} as ViewCatalogProductsContextShape
);

export const initialListViewDataState: State = {
  skip: 0,
  take: 100, // Default for list view (selectedView starts at 0) - picture view will override to 25
  sort: [{ field: 'itemNum', dir: 'asc' }],
};

export const initialPictureViewDataState: State = {
  skip: 0,
  take: 25,
  sort: [{ field: 'itemNum', dir: 'asc' }],
};

export const ViewCatalogProductsContextProvider: FC<ViewCatalogProductsContextProps> = ({
  children,
}: ViewCatalogProductsContextProps): ReactElement => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  //supplier user permission
  const isSupplierUser = usePermission({ permissions: IsSupplierUser });

  const selectedCatalogId = searchParams.get('catalogId');
  const initialCategoryIdParam = searchParams.get('categoryId');
  const initialOfflineSupplierIdParam = searchParams.get('offlineSupplierId');
  const initialCategoryId =
    initialCategoryIdParam && initialCategoryIdParam !== '' ? Number(initialCategoryIdParam) : null;
  const initialOfflineSupplierId =
    initialOfflineSupplierIdParam && initialOfflineSupplierIdParam !== ''
      ? Number(initialOfflineSupplierIdParam)
      : null;
  const hasAppliedUrlSupplierFilterRef = useRef(false);
  const hasAppliedUrlCategoryFilterRef = useRef(false);

  const [selectedCatalog, setSelectedCatalog] = useState<CatalogTreeViewItem | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedView, setSelectedView] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search.trim(), 500);
  const [productSelections, setProductSelections] = useState<ProductSelections>({});
  const [dataState, setDataState] = useState<State>({ ...initialListViewDataState });
  const [isProductPanelOpen, setIsProductPanelOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [itemFilter, setItemFilterState] = useState<DropDownCategories>(ProductFilterCategories[0]);
  const [pendingFilterToastKey, setPendingFilterToastKey] = useState<string | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [isAllCategoriesSelected, setIsAllCategoriesSelected] = useState<boolean>(true);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<number[]>([]);
  const [appliedCategoryIds, setAppliedCategoryIds] = useState<number[]>([]);
  const [appliedSupplierIds, setAppliedSupplierIds] = useState<number[]>([]);
  const [areAllCategoriesUnchecked, setAreAllCategoriesUnchecked] = useState<boolean>(false);
  const [areAllSuppliersUnchecked, setAreAllSuppliersUnchecked] = useState<boolean>(false);
  const hasAppliedSuppliersRef = useRef<boolean>(false);
  const currentSupplierCatalogIdRef = useRef<number | null>(null);
  const [suppliersInitialized, setSuppliersInitialized] = useState<boolean>(false);
  // Track page length (take) for each view separately
  const [listViewTake, setListViewTake] = useState<number>(100);
  const [pictureViewTake, setPictureViewTake] = useState<number>(25);
  //Swap Item
  const [isSwapItemModalOpen, setIsSwapItemModalOpen] = useState<boolean>(false);
  const [swapItemData, setSwapItemData] = useState<RecommendPageParams | null>(null);

  const {
    data: catalogs = [],
    isLoading: loadingCatalogs,
    isFetching: isFetchingCatalogs,
  } = useGetCatalogsQuery();

  const {
    data: categories,
    isLoading: loadingCategories,
    isFetching: isFetchingCategories,
    refetch: fetchCategories,
  } = useGetCategoryQuery(getNumericCatalogId(get(selectedCatalog, 'catalogId', null)));

  // Helper function to mark categories as checked based on applied IDs
  const markCategoriesChecked = useCallback(
    (items: TreeViewItem[], appliedIds: number[], allUnchecked: boolean): TreeViewItem[] => {
      // If all are explicitly unchecked, uncheck everything
      if (allUnchecked) {
        return items.map((item) => {
          const processedItem = { ...item, checked: false, indeterminate: false };
          if (item.items && item.items.length > 0) {
            processedItem.items = markCategoriesChecked(item.items, appliedIds, allUnchecked);
          }
          return processedItem;
        });
      }

      // Otherwise, use the normal logic: empty array = all selected
      const allSelected = appliedIds.length === 0;
      return items.map((item) => {
        if (item.isSelectAll) {
          return { ...item, checked: allSelected };
        }
        const categoryId = parseInt(item.id.replace('category-', ''), 10);
        const isChecked = allSelected || appliedIds.includes(categoryId);
        const processedItem = { ...item, checked: isChecked };
        if (item.items && item.items.length > 0) {
          processedItem.items = markCategoriesChecked(item.items, appliedIds, allUnchecked);
        }
        return processedItem;
      });
    },
    []
  );

  // Memoized categories with applied checked state
  const categoriesWithAppliedState = useMemo(() => {
    if (!categories) return undefined;
    return markCategoriesChecked(categories, appliedCategoryIds, areAllCategoriesUnchecked);
  }, [categories, appliedCategoryIds, areAllCategoriesUnchecked, markCategoriesChecked]);

  const isOfflineCatalog = useMemo(
    () => !!(selectedCatalog && selectedCatalog.catalogType === 3),
    [selectedCatalog]
  );

  const catalogId = getNumericCatalogId(selectedCatalog?.catalogId);
  const { data: suppliers = [], isLoading: isLoadingSuppliers } = useGetOfflineSuppliersApi(
    catalogId,
    isOfflineCatalog
  );

  const productQueryOptions = useMemo(() => {
    const baseOptions: ProductListFilterRequest = {
      catalogId: getNumericCatalogId(selectedCatalog?.catalogId) ?? 0,
      queryOptions: modifyRequest(dataState, ['lastPurchaseDate'], undefined, [
        'itemNum',
        'brandName',
        'description',
        'companyName',
        'uom',
        'casePack',
        'manufacturerNumber',
        'weight',
      ]),
    };

    if (isOfflineCatalog) {
      if (isLoadingSuppliers || !suppliersInitialized) {
        // Suppliers not yet ready — fire immediately with all suppliers (empty array = no filter)
        baseOptions.categoryIds = selectedCategories;
        baseOptions.supplierIds = [];
      } else {
        // Suppliers fully loaded — apply user selection logic
        const allAvailableSupplierIds = suppliers.map((s) => s.offlineSupplierId);

        const sortedSelected = [...selectedSupplierIds].sort((a, b) => a - b);
        const sortedAvailable = [...allAvailableSupplierIds].sort((a, b) => a - b);
        const allSuppliersSelected =
          sortedSelected.length === sortedAvailable.length &&
          sortedSelected.length > 0 &&
          sortedSelected.every((id, index) => id === sortedAvailable[index]);

        const noSuppliersSelected = selectedSupplierIds.length === 0;

        if (noSuppliersSelected) {
          baseOptions.categoryIds = [];
        } else if (allSuppliersSelected) {
          baseOptions.categoryIds = selectedCategories;
          baseOptions.supplierIds = [];
        } else {
          baseOptions.categoryIds = selectedCategories;
          baseOptions.supplierIds = selectedSupplierIds;
        }
      }
    } else {
      // For non-offline catalogs, normal behavior
      baseOptions.categoryIds = selectedCategories;
    }

    return baseOptions;
  }, [
    selectedCatalog?.catalogId,
    selectedCategories,
    selectedSupplierIds,
    dataState,
    isOfflineCatalog,
    suppliers,
    isLoadingSuppliers,
    suppliersInitialized,
  ]);

  const shouldEnableProductsQuery = useMemo(() => {
    if (isEmpty(selectedCatalog)) {
      return false;
    }
    // Wait for categories for all catalog types (including offline).
    // For offline catalogs the supplier dependency is removed — productQueryOptions handles
    // the supplierIds field by passing [] while suppliers are still loading.
    if (
      (!areAllCategoriesUnchecked && isEmpty(selectedCategories)) ||
      loadingCategories ||
      isEmpty(categories) ||
      isFetchingCategories
    ) {
      return false;
    }
    return true;
  }, [
    selectedCatalog,
    areAllCategoriesUnchecked,
    selectedCategories,
    loadingCategories,
    categories,
    isFetchingCategories,
  ]);

  const {
    data: productsResponse,
    isLoading: loadingProducts,
    isFetching: isFetchingProducts,
    refetch: fetchProducts,
    isEnabled: isProductsQueryEnabled,
  } = useGetProductsQuery({
    request: productQueryOptions,
    searchText: debouncedSearch,
    enabled: shouldEnableProductsQuery,
  });

  const itemFilterRef = useRef(itemFilter);
  itemFilterRef.current = itemFilter;

  const setItemFilter = useCallback<SetState<DropDownCategories>>(
    (value) => {
      const nextValue = typeof value === 'function' ? value(itemFilterRef.current) : value;
      const attributesFilterKey = getAttributesFilterKey(dataState.filter);
      const shouldUpdateGridFilter =
        nextValue.value !== itemFilterRef.current.value ||
        shouldClearAttributesForContractedItems(nextValue.value, attributesFilterKey);

      if (shouldUpdateGridFilter) {
        setDataState((prev) => {
          let { filter } = prev;

          if (shouldClearAttributesForContractedItems(nextValue.value, attributesFilterKey)) {
            filter = removeFieldFromFilter(filter, 'attributes') ?? EMPTY_GRID_FILTER;
          }

          return {
            ...prev,
            skip: 0,
            filter: applyItemFilterToGridFilter(filter, nextValue.value),
          };
        });

        if (shouldClearAttributesForContractedItems(nextValue.value, attributesFilterKey)) {
          setPendingFilterToastKey('attributeFilterCleared');
        }
      }

      setItemFilterState(nextValue);
    },
    [dataState.filter]
  );

  const { mutateAsync: updateCart, isPending: isUpdatingCart } = useUpdateCartMutation({
    onSuccess: (_, request) => {
      // Calculate actual count from the items that were sent to the API
      toast.success(`Added ${request.items.length} item(s) to cart successfully.`, {
        autoClose: 3000,
      });
    },
    onError: () => {
      toast.error('Unable to add items to Cart. Please try again later."');
    },
  });

  const loadingProductsCombined = useMemo(
    () =>
      loadingProducts ||
      isFetchingProducts ||
      isFetchingCategories ||
      loadingCategories ||
      isFetchingCatalogs ||
      loadingCatalogs ||
      isUpdatingCart ||
      (!isProductsQueryEnabled &&
        some(selectedCategories, (categorySelection: TreeViewItem) => categorySelection.checked)),
    [
      loadingProducts,
      isFetchingProducts,
      isFetchingCategories,
      loadingCategories,
      isFetchingCatalogs,
      loadingCatalogs,
      isUpdatingCart,
      isProductsQueryEnabled,
      selectedCategories,
    ]
  );

  useEffect(() => {
    if (!pendingFilterToastKey || loadingProductsCombined) {
      return;
    }

    toast.info(t(pendingFilterToastKey), { autoClose: 3000 });
    setPendingFilterToastKey(null);
  }, [pendingFilterToastKey, loadingProductsCombined, t]);

  const {
    data: productDetails,
    isLoading: isLoadingProductDetails,
    isFetching: fetchingProductDetails,
    refetch: fetchProductDetails,
  } = useGetProductDetailsQuery(
    get(selectedProduct, 'productId', null),
    get(selectedProduct, 'catalogId', null),
    get(selectedProduct, 'categoryId', null)
  );

  const loadingProductDetails = isLoadingProductDetails || fetchingProductDetails;

  useEffect(() => {
    if (!isEmpty(catalogs) && !loadingCatalogs && isEmpty(selectedCatalog)) {
      const firstCatalog: CatalogTreeViewItem | null = get(catalogs, '0.items.0', null);
      if (selectedCatalogId) {
        // Search through all catalog groups and their items arrays
        let foundCatalog: CatalogTreeViewItem | null = null;
        for (const catalogGroup of catalogs) {
          if (catalogGroup.items && Array.isArray(catalogGroup.items)) {
            const catalog = catalogGroup.items.find(
              (catalog) => Number(catalog.catalogId || 0) === Number(selectedCatalogId)
            );
            if (catalog) {
              foundCatalog = catalog;
              break;
            }
          }
        }

        if (foundCatalog) {
          setSelectedCatalog(foundCatalog);
        } else {
          setSelectedCatalog(firstCatalog);
        }
      } else {
        setSelectedCatalog(firstCatalog);
      }
    }
  }, [catalogs, loadingCatalogs, selectedCatalog, selectedCatalogId]);

  // Track the current catalog ID to detect catalog changes
  const currentCatalogIdRef = useRef<number | null>(null);
  const hasAppliedCategoriesRef = useRef<boolean>(false);

  const extractAllCategoryIds = (items: TreeViewItem[]): number[] => {
    const ids: number[] = [];
    const traverse = (item: TreeViewItem): void => {
      const categoryId = parseInt(item.id.replace('category-', ''), 10);
      if (!isNaN(categoryId)) {
        ids.push(categoryId);
      }

      if (item.items) {
        item.items.forEach((i) => {
          traverse(i);
        });
      }
    };

    for (const item of items) {
      traverse(item);
    }

    return ids;
  };

  useEffect(() => {
    if (!isEmpty(selectedCatalog) && !loadingCategories) {
      const catalogId = getNumericCatalogId(selectedCatalog?.catalogId);
      const catalogChanged = currentCatalogIdRef.current !== catalogId;

      if (catalogChanged) {
        currentCatalogIdRef.current = catalogId;
        // Reset applied categories and selectedCategories when catalog changes so that
        // shouldEnableProductsQuery blocks until fresh categories arrive for the new catalog.
        setAppliedCategoryIds([]);
        setSelectedCategories([]);
        setAreAllCategoriesUnchecked(false);
        hasAppliedCategoriesRef.current = false;
        fetchCategories().then((value: QueryObserverResult<TreeViewItem[] | undefined>): void => {
          const fetchedCategories = get(value, 'data', []) as TreeViewItem[];

          // Always initialize selectedCategories with all categories on catalog change or first load
          // Only use appliedCategoryIds if it has been set (after Apply was clicked)
          if (catalogChanged || !hasAppliedCategoriesRef.current) {
            const allCategoryIds = extractAllCategoryIds(
              fetchedCategories.filter(
                (item: TreeViewItem): boolean => !item.text.toLowerCase().includes('all-categories')
              )
            );

            const shouldApplyUrlCategoryFilter =
              initialCategoryId != null &&
              !Number.isNaN(initialCategoryId) &&
              !hasAppliedUrlCategoryFilterRef.current &&
              selectedCatalogId != null &&
              String(catalogId) === selectedCatalogId;

            if (shouldApplyUrlCategoryFilter && allCategoryIds.includes(initialCategoryId)) {
              setSelectedCategories([initialCategoryId]);
              setAppliedCategoryIds([initialCategoryId]);
              hasAppliedCategoriesRef.current = true;
              hasAppliedUrlCategoryFilterRef.current = true;
              setAreAllCategoriesUnchecked(false);
            } else {
              setSelectedCategories(allCategoryIds);
            }
          } else {
            // Use existing applied state (only if Apply was previously clicked)
            setSelectedCategories(appliedCategoryIds);
          }
        });
      }
    }
  }, [
    fetchCategories,
    loadingCategories,
    selectedCatalog,
    appliedCategoryIds,
    initialCategoryId,
    selectedCatalogId,
  ]);

  // Initialize selectedSupplierIds when suppliers are first loaded for offline catalogs
  useEffect(() => {
    if (isOfflineCatalog && !isEmpty(suppliers) && !isLoadingSuppliers) {
      const catalogId = getNumericCatalogId(selectedCatalog?.catalogId);
      const supplierCatalogChanged = currentSupplierCatalogIdRef.current !== catalogId;

      if (supplierCatalogChanged) {
        currentSupplierCatalogIdRef.current = catalogId;
        // Reset applied suppliers when catalog changes
        setAppliedSupplierIds([]);
        setAreAllSuppliersUnchecked(false);
        hasAppliedSuppliersRef.current = false;

        // Initialize selectedSupplierIds with all supplier IDs on catalog change or first load
        // Only use appliedSupplierIds if it has been set (after Apply was clicked)
        if (supplierCatalogChanged || !hasAppliedSuppliersRef.current) {
          const allSupplierIds = suppliers.map((s) => s.offlineSupplierId);

          const shouldApplyUrlSupplierFilter =
            initialOfflineSupplierId != null &&
            !Number.isNaN(initialOfflineSupplierId) &&
            !hasAppliedUrlSupplierFilterRef.current &&
            selectedCatalogId != null &&
            String(catalogId) === selectedCatalogId;

          if (shouldApplyUrlSupplierFilter && allSupplierIds.includes(initialOfflineSupplierId)) {
            setSelectedSupplierIds([initialOfflineSupplierId]);
            setAppliedSupplierIds([initialOfflineSupplierId]);
            hasAppliedSuppliersRef.current = true;
            hasAppliedUrlSupplierFilterRef.current = true;
            setAreAllSuppliersUnchecked(false);
          } else {
            setSelectedSupplierIds(allSupplierIds);
          }
        } else {
          // Use existing applied state (only if Apply was previously clicked)
          setSelectedSupplierIds(appliedSupplierIds);
        }
        // Mark suppliers as initialized for this catalog
        setSuppliersInitialized(true);
      }
    } else if (!isOfflineCatalog) {
      // When switching away from offline catalog, reset initialization state
      setSuppliersInitialized(false);
      currentSupplierCatalogIdRef.current = null;
    }
  }, [
    suppliers,
    isLoadingSuppliers,
    isOfflineCatalog,
    selectedCatalog?.catalogId,
    appliedSupplierIds,
    initialOfflineSupplierId,
    selectedCatalogId,
  ]);

  // Clear product selections when search is applied (debounced)
  useEffect(() => {
    setProductSelections({});
    setSelectedProduct(null);
    setIsProductPanelOpen(false);
  }, [debouncedSearch]);

  const handleSelectedItemsAddToCart = useCallback(async () => {
    const items = mapSelectionsToCartItems(productSelections, productsResponse?.items ?? []);
    await updateCart({
      items: items,
    });
    handlePostMessage({ cartUpdated: true });
    setProductSelections({});
    await fetchProducts();
  }, [productSelections, productsResponse?.items, updateCart, fetchProducts]);

  const {
    isOpen: isUnsavedSelectionOpen,
    handleConfirm: handleUnsavedConfirm,
    handleAddToCart: handleUnsavedAddToCart,
    handleClose: handleUnsavedClose,
    confirm: confirmUnsavedSelection,
    selectedItemsCount,
  } = useUnsavedSelection({
    productSelections,
    onAddToCart: handleSelectedItemsAddToCart,
  });

  const handleSingleItemAddToCart = useCallback(
    async (items: CartItem) => {
      await updateCart({
        items: [items],
      });
      handlePostMessage({ cartUpdated: true });
      // Only the added item is deselected; the toolbar bulk add is what clears everything.
      setProductSelections((prev) => removeProductSelection(prev, getCartItemProductKey(items)));
      await fetchProducts();
    },
    [updateCart, fetchProducts]
  );

  const handleConfirm = useCallback(
    async (callback: () => void | Promise<void>): Promise<void> => {
      const proceed = await confirmUnsavedSelection(callback);
      if (!proceed) {
        return;
      }
    },
    [confirmUnsavedSelection]
  );

  const handleCatalogChange = useCallback(
    async (catalog: CatalogTreeViewItem | null): Promise<void> => {
      if (!isEmpty(catalog)) {
        await handleConfirm(() => {
          handlePostMessage({
            catalogId: catalog?.catalogId,
          });
          if (selectedView === 0) {
            setDataState((prev: State) => ({ ...initialListViewDataState, take: prev.take }));
          } else {
            setDataState((prev: State) => ({ ...initialPictureViewDataState, take: prev.take }));
          }
          setItemFilterState(ProductFilterCategories[0]);
          setSelectedCatalog(catalog);
          setSelectedCategoryIds([]);
          setIsAllCategoriesSelected(true);
          setSelectedSupplierIds([]);
          // Reset applied categories and suppliers when catalog changes
          setAppliedCategoryIds([]);
          setAreAllCategoriesUnchecked(false);
          setAppliedSupplierIds([]);
          setAreAllSuppliersUnchecked(false);
          hasAppliedSuppliersRef.current = false;
          // Reset supplier catalog ref and initialization state to ensure supplier initialization effect runs on next catalog switch
          currentSupplierCatalogIdRef.current = null;
          setSuppliersInitialized(false);
          // Only clear search if there were selected items (user confirmed clearing them)
          // Otherwise, preserve search text to filter by updated catalog
          if (Object.values(productSelections).some((p) => p.selected)) {
            setSearch('');
          }
          // Clear product selections when catalog changes
          setProductSelections({});
          setSelectedProduct(null);
          setIsProductPanelOpen(false);
        });
      }
    },
    [productSelections, handleConfirm, selectedView]
  );

  const onDataStateChange = useCallback(
    async (event: GridDataStateChangeEvent | State) => {
      // Handle both GridDataStateChangeEvent (from GridWrapper) and State (from CardGridWrapper)
      const newDataState = 'dataState' in event ? event.dataState : event;

      // Show unsaved changes modal if there are selected items, then update dataState
      await handleConfirm(() => {
        setSelectedProduct(null);
        setIsProductPanelOpen(false);
        setProductSelections({});
        if (newDataState) {
          const previousAttributesKey = getAttributesFilterKey(dataState.filter);
          const nextAttributesKey = getAttributesFilterKey(
            newDataState.filter !== undefined ? newDataState.filter : dataState.filter
          );
          const shouldResetItemFilter =
            shouldResetItemFilterForAttributes(previousAttributesKey, nextAttributesKey) &&
            itemFilter.value === 1;

          if (shouldResetItemFilter) {
            setItemFilterState(ProductFilterCategories[0]);
            setPendingFilterToastKey('contractedItemsFilterReset');
          }

          setDataState((prev) => {
            const updated = { ...prev, ...newDataState };
            if (shouldResetItemFilter) {
              updated.skip = 0;
              updated.filter = clearContractedItemGridFilter(updated.filter);
            }
            // Save the take value for the current view when it changes
            if (newDataState.take !== undefined && newDataState.take !== prev.take) {
              if (selectedView === 0) {
                setListViewTake(newDataState.take);
              } else {
                setPictureViewTake(newDataState.take);
              }
            }
            return updated;
          });
        }
      });
    },
    [handleConfirm, selectedView, dataState.filter, itemFilter.value]
  );

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const productKey = event?.dataItem?.productKey;
    if (!productKey) return;

    setProductSelections((prev: ProductSelections) => {
      const current = prev[productKey] || { selected: false, qty: 0 };
      const newSelected = !current.selected;
      return {
        ...prev,
        [productKey]: {
          selected: newSelected,
          qty: newSelected && current.qty === 0 ? 1 : newSelected ? current.qty : 0,
        },
      };
    });
  }, []);

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;

      setProductSelections((prev: ProductSelections) => {
        // Get current page's products based on dataState
        const skip = dataState.skip || 0;
        const take = dataState.take || 100;
        const currentPageProducts = get(productsResponse, 'items', []).slice(skip, skip + take);

        // Create updated selections object starting with existing selections
        const updated: ProductSelections = { ...prev };

        // Update all products on the current page
        currentPageProducts.forEach((product: Product) => {
          const { productKey } = product;
          const current = prev[productKey] || { selected: false, qty: 0 };
          updated[productKey] = {
            selected: checked,
            qty: checked && current.qty === 0 ? 1 : checked ? current.qty : 0,
          };
        });

        return updated;
      });
    },
    [dataState.skip, dataState.take, productsResponse]
  );

  const updateProductSelection = useCallback(
    (productKey: string, updates: Partial<ProductSelectionState>) => {
      setProductSelections((prev) => {
        const current = prev[productKey] || { selected: false, qty: 0 };
        return {
          ...prev,
          [productKey]: { ...current, ...updates },
        };
      });
    },
    []
  );

  const getProductSelection = useCallback(
    (productKey: string): ProductSelectionState =>
      productSelections[productKey] || { selected: false, qty: 0 },
    [productSelections]
  );

  // Helper function to mark suppliers as checked based on applied IDs
  const markSuppliersChecked = useCallback(
    (
      suppliersList: OfflineSupplier[],
      appliedIds: number[],
      allUnchecked: boolean
    ): OfflineSupplier[] => {
      // If all are explicitly unchecked, uncheck everything
      if (allUnchecked) {
        return suppliersList.map((supplier) => ({
          ...supplier,
          checked: false,
        }));
      }

      // Otherwise, use the normal logic: empty array = all selected
      const allSelected = appliedIds.length === 0;
      return suppliersList.map((supplier) => ({
        ...supplier,
        checked: allSelected || appliedIds.includes(supplier.offlineSupplierId),
      }));
    },
    []
  );

  // Create supplier checkbox data with applied checked state
  const supplierData: OfflineSupplier[] = useMemo(
    () => markSuppliersChecked(suppliers, appliedSupplierIds, areAllSuppliersUnchecked),
    [suppliers, appliedSupplierIds, areAllSuppliersUnchecked, markSuppliersChecked]
  );

  const handleSupplierChange = useCallback(
    async (selectedSupplierIds: number[]) => {
      await handleConfirm(() => {
        // Determine state:
        // - If selectedSupplierIds contains all available supplier IDs, treat as "all selected"
        // - If selectedSupplierIds is empty, treat as "all unchecked"
        // - Otherwise, specific suppliers are selected

        // Get all available supplier IDs
        const allAvailableSupplierIds = suppliers.map((s) => s.offlineSupplierId);

        // Check if all suppliers are selected by comparing arrays
        const sortedSelected = [...selectedSupplierIds].sort((a, b) => a - b);
        const sortedAvailable = [...allAvailableSupplierIds].sort((a, b) => a - b);
        const allSelected =
          sortedSelected.length === sortedAvailable.length &&
          sortedSelected.length > 0 &&
          sortedSelected.every((id, index) => id === sortedAvailable[index]);

        // All unchecked if empty array
        const allUnchecked = selectedSupplierIds.length === 0;

        // For appliedSupplierIds: use empty array if all selected (for UI state), otherwise use actual IDs
        const newAppliedIds = allSelected ? [] : selectedSupplierIds;

        // Update applied state (this is called only when Apply is clicked)
        setAppliedSupplierIds(newAppliedIds);
        setAreAllSuppliersUnchecked(allUnchecked);
        // Mark that suppliers have been applied
        hasAppliedSuppliersRef.current = true;
        // Update selected suppliers for filtering (use actual supplier IDs for API)
        setSelectedSupplierIds(selectedSupplierIds);

        // Reset pagination to default skip value
        setDataState(
          (prev: State): State => ({
            ...prev,
            skip: 0,
          })
        );

        // Only clear search if there were selected items (user confirmed clearing them)
        // Otherwise, preserve search text to filter by updated supplier
        if (Object.values(productSelections).some((p) => p.selected)) {
          setSearch('');
        }
        // Clear product selections when supplier changes
        setProductSelections({});
        setSelectedProduct(null);
        setIsProductPanelOpen(false);
      });
    },
    [productSelections, handleConfirm, suppliers]
  );

  // Derived selection helpers from productSelections
  const hasSelectedItems = useMemo(
    () => Object.values(productSelections).some((p) => p.selected),
    [productSelections]
  );

  const clearSelections = useCallback(() => {
    setProductSelections((prev) => {
      const updated: ProductSelections = {};
      Object.keys(prev).forEach((productKey) => {
        updated[productKey] = {
          selected: false,
          qty: 0,
        };
      });
      return updated;
    });
  }, []);

  const getSelectedItems = useCallback(
    () =>
      Object.entries(productSelections)
        .filter(([_, selection]) => selection.selected)
        .map(([productKey]) => ({ productKey })),
    [productSelections]
  );

  const { mutateAsync: addToFavorites, isPending: isAddingToFavorites } = useAddToFavoritesMutation(
    {}
  );

  const { data: favoriteFolders = [] } = useGetFavoriteFoldersQuery(!isSupplierUser);

  const folderOptions = useMemo(
    () =>
      favoriteFolders.map((folder) => ({
        id: folder.id,
        name: folder.description,
      })),
    [favoriteFolders]
  );

  const handleAddToFavorites = useCallback(
    async (folderId: number) => {
      try {
        if (!selectedCatalog?.catalogId) {
          toast.error('Please select a catalog first.');
          return;
        }

        const selectedItems = getSelectedItems();
        let selectedProducts: Product[] = [];
        if (isProductPanelOpen) {
          selectedProducts.push(selectedProduct as Product);
        } else {
          selectedProducts = get(productsResponse, 'items', []).filter((p: Product) =>
            selectedItems.some((item) => item.productKey === p.productKey)
          );
        }
        const folder = folderOptions.find((f) => f.id === folderId);
        const folderName = folder?.name || 'folder';

        const favouriteProduct = mapProductsToFavoriteItems(selectedProducts);

        await addToFavorites({
          favouriteProduct,
          folderId,
        });

        toast.success(`Selected item(s) have been added to ${folderName}.`, {
          autoClose: 3000,
        });
        clearSelections();
        if (!isEmpty(selectedProduct)) {
          await Promise.all([fetchProductDetails(), fetchProducts()]);
        } else {
          await fetchProducts();
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to add item(s) to favorites. Please try again.';
        toast.error(errorMessage);
      }
    },
    [
      selectedCatalog?.catalogId,
      getSelectedItems,
      isProductPanelOpen,
      folderOptions,
      addToFavorites,
      clearSelections,
      fetchProducts,
      fetchProductDetails,
      selectedProduct,
      productsResponse,
    ]
  );

  const handleCategoryChange = useCallback(
    async (checkedItems: string[]): Promise<void> => {
      await handleConfirm(() => {
        // Extract category IDs from checkedItems
        const categoryIds = checkedItems
          .filter((itemId) => itemId !== 'all-categories')
          .map((itemId) => {
            const match = itemId.match(/^category-(\d+)$/);
            return match ? parseInt(match[1], 10) : null;
          })
          .filter((id): id is number => id !== null);

        // Determine if all categories are selected by comparing with total available categories
        // If all category IDs are present, treat as "all selected"
        let allSelected = false;
        let allUnchecked = false;

        if (checkedItems.length === 0) {
          // Empty array means all unchecked
          allUnchecked = true;
        } else if (categories) {
          // Extract all available category IDs to compare
          const extractAllCategoryIds = (items: TreeViewItem[]): number[] => {
            const ids: number[] = [];
            items.forEach((item) => {
              // Skip "All Categories" item
              if (item.isSelectAll) {
                return;
              }
              const match = item.id.match(/^category-(\d+)$/);
              if (match) {
                ids.push(parseInt(match[1], 10));
              }
              // Recursively extract child category IDs
              if (item.items) {
                ids.push(...extractAllCategoryIds(item.items));
              }
            });
            return ids;
          };
          const allAvailableCategoryIds = extractAllCategoryIds(categories);

          // Check if all available categories are in the checkedItems
          // Sort both arrays for comparison
          const sortedChecked = [...categoryIds].sort((a, b) => a - b);
          const sortedAvailable = [...allAvailableCategoryIds].sort((a, b) => a - b);
          allSelected =
            sortedChecked.length === sortedAvailable.length &&
            sortedChecked.every((id, index) => id === sortedAvailable[index]);
        }

        const newAppliedIds = allSelected ? [] : categoryIds;

        // For selectedCategories (used in products query), use the actual category IDs
        // When "All" is selected, use all category IDs; when unchecked, use empty array
        const newSelectedCategories = allUnchecked ? [] : categoryIds;

        // Update applied state (this is called only when Apply is clicked)
        setAppliedCategoryIds(newAppliedIds);
        setAreAllCategoriesUnchecked(allUnchecked);
        // Mark that categories have been applied
        hasAppliedCategoriesRef.current = true;
        // Update selected categories for filtering (use actual category IDs for API)
        setSelectedCategories(newSelectedCategories);
        setDataState((prev) => ({
          ...prev,
          skip: 0,
        }));

        // Only clear search if there were selected items (user confirmed clearing them)
        // Otherwise, preserve search text to filter by updated category
        if (Object.values(productSelections).some((p) => p.selected)) {
          setSearch('');
        }
        // Clear product selections when category changes
        setProductSelections({});
        setSelectedProduct(null);
        setIsProductPanelOpen(false);
      });
    },
    [productSelections, handleConfirm, categories]
  );

  const handleOpenProductPanel = useCallback(() => {
    setIsProductPanelOpen(true);
  }, []);

  const handleCloseProductPanel = useCallback(() => {
    setIsProductPanelOpen(false);
    // Clear selected product when panel closes
    queryClient.removeQueries({ queryKey: ['productDetails'] });
    setSelectedProduct(null);
  }, [queryClient]);

  // Ref to track if a product details fetch is in progress (prevents multiple concurrent calls)
  const isFetchingProductDetailsRef = useRef<boolean>(false);
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState<boolean>(false);

  const handleViewProductDetails = useCallback(
    async (e: MouseEvent, product: Product): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent multiple concurrent calls using ref (more reliable than state for race conditions)
      if (isFetchingProductDetailsRef.current || loadingProductDetails) {
        return;
      }

      try {
        isFetchingProductDetailsRef.current = true;
        setIsFetchingProductDetails(true);

        // Cancel any existing product details queries before starting new one
        await queryClient.cancelQueries({
          queryKey: ['catalogProducts', 'productDetails'],
        });

        // Set the selected product immediately
        setSelectedProduct(product);

        // Fetch product details directly using queryClient.fetchQuery
        // This bypasses the stale closure issue since we pass the product data directly
        await queryClient.fetchQuery({
          queryKey: queryKeys.product.productDetails(
            product.productId,
            product.catalogId,
            product.categoryId
          ),
          queryFn: fetchProductDetailsQuery,
        });

        // Only open panel after successful fetch
        setIsProductPanelOpen(true);
      } catch (error) {
        // Handle cancellation silently (user clicked another product)
        if ((error as Error)?.name !== 'CancelledError') {
          console.error('Failed to fetch product details:', error);
        }
      } finally {
        isFetchingProductDetailsRef.current = false;
        setIsFetchingProductDetails(false);
      }
    },
    [loadingProductDetails, queryClient]
  );

  const onItemChange = useCallback((event: GridItemChangeEvent) => {
    const { field, dataItem, value } = event;
    const productKey = dataItem?.productKey;
    if (!productKey) return;

    if (field === 'selected') {
      const newSelected = value as boolean;
      setProductSelections((prev) => {
        const current = prev[productKey] || { selected: false, qty: 0 };
        return {
          ...prev,
          [productKey]: {
            selected: newSelected,
            qty: newSelected && current.qty === 0 ? 1 : newSelected ? current.qty : 0,
          },
        };
      });
    } else if (field === 'qty') {
      const newQty = value as number;
      setProductSelections((prev) => ({
        ...prev,
        [productKey]: {
          selected: newQty !== 0,
          qty: newQty,
        },
      }));
    }
  }, []);

  const categoryDropdownPlaceholder = useMemo((): string => {
    if (loadingCategories) {
      return t('Loading...');
    }
    if (isEmpty(categories)) {
      return t('No Categories');
    }
    return t('All Categories');
  }, [categories, loadingCategories, t]);

  const catalogDropdownPlaceholder = useMemo(
    () => getCatalogDropdownPlaceholder(catalogs, loadingCatalogs, t),
    [catalogs, loadingCatalogs, t]
  );

  // enable unsaved changes modal by passing isDirty to usePostMessage
  const isDirty = useMemo(
    () => Object.values(productSelections).some((p) => p.qty !== 0),
    [productSelections]
  );

  usePostMessage({ isDirty });

  const handleSearchChange = useCallback(
    async (value: string) => {
      await handleConfirm(() => {
        setDataState(
          (prev: State): State => ({
            ...prev,
            skip: 0,
          })
        );
        setSearch(value);
      });
    },
    [handleConfirm]
  );

  const shouldPreserveSort = useCallback((currentSortArray: Array<SortDescriptor>): boolean => {
    // const currentSortArray: Array<SortDescriptor> = get(dataState, 'sort', [])
    const fields: string = currentSortArray
      .map((sort: SortDescriptor): string => sort.field)
      .join('');

    return new RegExp(`(${PRESERVE_SORT_FIELDS.join('|')})`, 'gi').test(fields);
  }, []);

  const clearFilters = useCallback(() => {
    setItemFilterState(ProductFilterCategories[0]);
    setPendingFilterToastKey(null);
    setSearch('');
    // Reset pagination to default take value for current view
    if (selectedView === 0) {
      setDataState((prev) => ({
        ...initialListViewDataState,
        take: prev.take,
      }));
    } else {
      setDataState((prev) => ({
        ...initialPictureViewDataState,
        take: prev.take,
      }));
    }
  }, [selectedView]);

  const hasActiveFilters = useMemo(() => {
    const filters = dataState?.filter?.filters;

    // Check if grid filters have actual filter conditions (not empty nested arrays)
    const hasGridFilters =
      filters?.some((filter: CompositeFilterDescriptor | FilterDescriptor) =>
        'filters' in filter ? filter.filters.length > 0 : true
      ) ?? false;

    const hasSearchValue = search.trim().length > 0;

    // Check if sorting differs from default (itemNum asc)
    const currentSort = dataState?.sort?.[0];
    const defaultSort = initialListViewDataState.sort?.[0];
    const hasNonDefaultSort =
      currentSort !== undefined &&
      (currentSort.field !== defaultSort?.field || currentSort.dir !== defaultSort?.dir);

    const hasItemFilter = itemFilter.value !== 0;

    return hasGridFilters || hasSearchValue || hasNonDefaultSort || hasItemFilter;
  }, [dataState?.filter?.filters, dataState?.sort, search, itemFilter.value]);

  // Helper function to get the take value for a specific view
  const getTakeForView = useCallback(
    (viewIndex: number): number => {
      if (viewIndex === 0) {
        return listViewTake;
      } else {
        return pictureViewTake;
      }
    },
    [listViewTake, pictureViewTake]
  );

  const value = useMemo(
    () => ({
      catalogs,
      loadingCatalogs,

      categories: categoriesWithAppliedState,
      loadingCategories,
      fetchCategories,

      products: get(productsResponse, 'items', []).map(
        (item: Product): Product => ({
          ...item,
          itemNum: defaultTo(get(item, 'itemNum', ''), '').trim(),
          brandName: defaultTo(get(item, 'brandName', ''), '').trim(),
          description: defaultTo(get(item, 'description', ''), '').trim(),
          companyName: defaultTo(get(item, 'companyName', ''), '').trim(),
          uom: defaultTo(get(item, 'uom', ''), '').trim(),
          casePack: defaultTo(get(item, 'casePack', ''), '').trim(),
          manufacturerNumber: defaultTo(get(item, 'manufacturerNumber', ''), '').trim(),
          weight: defaultTo(get(item, 'weight', ''), '').trim(),
          lastPurchaseDate: defaultTo(get(item, 'lastPurchaseDate', ''), '').trim(),
        })
      ),
      productsTotal: get(productsResponse, 'totalRecords', 0),
      loadingProducts: loadingProductsCombined,
      fetchProducts,

      productDetails,
      loadingProductDetails,
      fetchProductDetails,

      search,
      setSearch,
      handleSearchChange,

      selectedView,
      setSelectedView,

      selectedCatalog,
      setSelectedCatalog,

      selectedCategories,
      setSelectedCategories,
      appliedCategoryIds,
      setAppliedCategoryIds,

      productSelections,
      setProductSelections,

      selectedProduct,
      setSelectedProduct,

      handleCatalogChange,
      handleCategoryChange,
      onSelectionChange,
      onHeaderSelectionChange,
      updateProductSelection,
      getProductSelection,
      hasSelectedItems,
      clearSelections,
      getSelectedItems,
      dataState,
      setDataState,
      onItemChange,
      onDataStateChange,
      handleOpenProductPanel,
      handleCloseProductPanel,
      handleViewProductDetails,
      isFetchingProductDetails,

      categoryDropdownPlaceholder,
      catalogDropdownPlaceholder,

      isProductPanelOpen,
      setIsProductPanelOpen,

      folderOptions,
      handleAddToFavorites,
      isAddingToFavorites,
      itemFilter,
      setItemFilter,
      setPendingFilterToastKey,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      isUpdatingCart,
      clearFilters,
      hasActiveFilters,
      handleConfirm,

      selectedCategoryIds,
      setSelectedCategoryIds,
      isAllCategoriesSelected,
      setIsAllCategoriesSelected,
      isOfflineCatalog,
      supplierData,
      handleSupplierChange,
      isLoadingSuppliers,
      appliedSupplierIds,
      setAppliedSupplierIds,
      getTakeForView,

      shouldPreserveSort,
      // Swap Item
      isSwapItemModalOpen,
      setIsSwapItemModalOpen,
      swapItemData,
      setSwapItemData,
      //supplier permission
      isSupplierUser,
    }),
    [
      catalogs,
      loadingCatalogs,
      categoriesWithAppliedState,
      loadingCategories,
      fetchCategories,
      productsResponse,
      loadingProductsCombined,
      fetchProducts,
      productDetails,
      loadingProductDetails,
      fetchProductDetails,
      search,
      handleSearchChange,
      selectedView,
      selectedCatalog,
      selectedCategories,
      appliedCategoryIds,
      productSelections,
      selectedProduct,
      handleCatalogChange,
      handleCategoryChange,
      onSelectionChange,
      onHeaderSelectionChange,
      updateProductSelection,
      getProductSelection,
      hasSelectedItems,
      clearSelections,
      getSelectedItems,
      dataState,
      onItemChange,
      onDataStateChange,
      handleOpenProductPanel,
      handleCloseProductPanel,
      handleViewProductDetails,
      isFetchingProductDetails,
      categoryDropdownPlaceholder,
      catalogDropdownPlaceholder,
      isProductPanelOpen,
      folderOptions,
      handleAddToFavorites,
      isAddingToFavorites,
      itemFilter,
      setItemFilter,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      isUpdatingCart,
      clearFilters,
      hasActiveFilters,
      handleConfirm,
      selectedCategoryIds,
      isAllCategoriesSelected,
      isOfflineCatalog,
      supplierData,
      handleSupplierChange,
      isLoadingSuppliers,
      appliedSupplierIds,
      getTakeForView,
      shouldPreserveSort,
      isSwapItemModalOpen,
      setIsSwapItemModalOpen,
      swapItemData,
      setSwapItemData,
      isSupplierUser,
    ]
  );

  return (
    <ViewCatalogProductsContext.Provider value={value}>
      {children}
      <UnsavedSelectionDialog
        open={isUnsavedSelectionOpen}
        onClose={handleUnsavedClose}
        onConfirm={handleUnsavedConfirm}
        onAddToCart={handleUnsavedAddToCart}
        selectedItemsCount={selectedItemsCount}
      />
    </ViewCatalogProductsContext.Provider>
  );
};
